import { useCallback, useEffect, useRef, useState } from 'react';
import { getTableId, getValues } from 'lib/services/valueLists';
import { getPriorityTranslation } from 'lib/helpers';

const PAGE_NUMBER = 30;

const useSmartFieldsValues = ({ valueListRelated, language, useSmartFields }) => {
    const [valueListItems, setValueListItems] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const offset = useRef(0);
    const tableIdCache = useRef(null);

    const loadValues = useCallback(
        (append = false) => {
            if (!valueListRelated) return Promise.reject('No valueListRelated');

            setIsLoading(true);

            // Get table ID first, then get values
            const getTableIdPromise = tableIdCache.current
                ? Promise.resolve({ data: tableIdCache.current })
                : getTableId(valueListRelated);

            return getTableIdPromise
                .then(({ data }) => {
                    tableIdCache.current = data;
                    return getValues(tableIdCache.current?.id, offset.current);
                })
                .then(({ data }) => {
                    const mappedData = data.map((item) => ({
                        label: getPriorityTranslation(item, 'description', language.toUpperCase()),
                        value: item.id,
                        order: item.order,
                    }));

                    setValueListItems((prev) => (append ? [...prev, ...mappedData] : mappedData));
                    setHasMore(data.length >= PAGE_NUMBER);
                    offset.current += PAGE_NUMBER;
                    setIsLoading(false);

                    // Scroll to bottom
                    const sidebar = document.querySelector(
                        '[class*="HoiPoi__DrawerContent__scroll"]',
                    );
                    if (sidebar) {
                        sidebar.scrollTop = sidebar.scrollHeight;
                    }

                    return mappedData;
                })
                .catch((err) => {
                    console.error(err);
                    setValueListItems([]);
                    setHasMore(false);
                    setIsLoading(false);
                    throw err;
                });
        },
        [valueListRelated, language],
    );

    const loadMore = useCallback(() => {
        if (isLoading || !hasMore) return;
        loadValues(true);
    }, [isLoading, hasMore, loadValues]);

    useEffect(() => {
        offset.current = 0;
        tableIdCache.current = null;
        setValueListItems([]);
        setHasMore(false);

        if (valueListRelated && useSmartFields) {
            loadValues(false);
        }
    }, [valueListRelated, language, useSmartFields, loadValues]);

    return { valueListItems, hasMore, loadMore, isLoading };
};

export default useSmartFieldsValues;
